// Copyright (c) StrangeBee 2023

import {
    OrganisationInput,
    OrganisationOutput,
    OrganisationController,
    OrganisationLinksOutput,
    SharingProfileOutput,
    FilterInput,
} from 'thehive-sdk';
import { OrgValuesForm } from './OrgCreate/OrgForm';

export interface OrganisationEventList {
    onListLinkableOrganisations: (filters?: FilterInput[]) => Promise<OrganisationOutput[]>;
    onListLinkedOrganisations: (orgId: string, filters?: FilterInput[]) => Promise<OrganisationOutput[]>;
    onListSharingProfiles: () => Promise<SharingProfileOutput[]>;
    onSetLinks: (links: OrganisationLinksOutput[], orgId: string) => Promise<any>;
    onCreate: (values: OrgValuesForm) => void;
    onEdit: (organisation: Partial<OrganisationInput>, orgId: string) => Promise<void>;
}

interface EventListParams {
    locale: (label: string, params?: any) => string;
    controller: OrganisationController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
}: EventListParams): OrganisationEventList => ({
    onListLinkableOrganisations: (filters?: FilterInput[]): Promise<OrganisationOutput[]> =>
        controller.list({
            selector: {
                head: [{ name: 'listOrganisation' }],
                initialFilters: filters,
                name: 'get-linkable-organisations',
            },
            sort: ['+name'],
        }),
    onListLinkedOrganisations: (orgId: string, filters?: FilterInput[]): Promise<OrganisationOutput[]> =>
        controller.list({
            selector: {
                head: [{ name: 'getOrganisation', idOrName: orgId }, { name: 'visible' }],
                initialFilters: filters,
                name: 'get-linked-organisations',
            },
        }),
    onListSharingProfiles: () => controller.getSharingProfile(),
    onSetLinks: (links, orgId) =>
        controller
            .setLinks(links, orgId)
            .then(() => onSuccess(false, locale('notification.organisation.link', { orgId })))
            .catch(onError),
    onCreate: (values: OrgValuesForm): void => {
        controller
            .create(values)
            .then(() => {
                onSuccess(false, locale('notification.organisation.create'));
            })
            .catch(onError);
    },
    onEdit: (organisation: Partial<OrganisationInput>, orgId: string): Promise<void> =>
        controller
            .edit<Partial<OrganisationInput>, void>(organisation, orgId)
            .then(() => onSuccess(false, locale('notification.organisation.edit')))
            .catch(onError),
});
